import { CloseDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import {
	resolveComponentTreePropertyValues,
	resolvePartConfiguratorTreeResultValues,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/partConfiguratorTreeResultValues.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import i18n from "i18next";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { PartConfiguratorTreeResult } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { faSquareCheck } from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { CatalogPartRevisionConfiguratorSelectionsAsyncView } from "src/components/views/erp/configurator/managing/catalogPart/configuratorSelections/CatalogPartRevisionConfiguratorSelectionsAsyncView.tsx";
import { CatalogPartTestConfiguratorResultsView } from "src/components/views/erp/configurator/configuratorTesting/catalogPart/CatalogPartTestConfiguratorResultsView.tsx";
import { PartConfiguratorTopLevelView } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorTopLevelView.tsx";
import { TestConfigurationPrintoutButton } from "src/components/views/erp/configurator/configuratorTesting/TestConfigurationPrintoutButton.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CatalogPartTestConfiguratorViewProps {
	productFamilyVersionId: number;
	catalogPartRevisionId: number;
	closeDialog?: CloseDialogFunc;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export const CatalogPartTestConfiguratorView = ({
	catalogPartRevisionId,
	productFamilyVersionId,
	closeDialog,
	onDataDirtyStateChanged,
}: CatalogPartTestConfiguratorViewProps) => {
	const { openDialog } = useGenericDialog();

	const configuratorType: PartConfiguratorType = {
		type: "catalogPartTest",
		catalogPartRevisionId: catalogPartRevisionId,
		productFamilyVersionId: productFamilyVersionId,
	};

	return (
		<PartConfiguratorTopLevelView
			configuratorType={configuratorType}
			onDataDirtyStateChanged={onDataDirtyStateChanged}
			onCompleted={async (result) => {
				if (result.type === "success") {
					await onSubmit(result.value);
				} else {
					await closeDialog?.({ confirmIfEdited: result.isEdited });
				}
			}}
			propertyModelFooterExtraComponents={({ resolvePropertyValues }) => (
				<TestConfigurationPrintoutButton
					configuratorType={configuratorType}
					resolvePropertyValues={resolvePropertyValues}
				/>
			)}
			footerExtraComponents={({ rootConfiguratorItem }) => (
				<>
					<TestConfigurationPrintoutButton
						configuratorType={configuratorType}
						resolvePropertyValues={() => resolveComponentTreePropertyValues(rootConfiguratorItem)}
					/>
					<AsyncButton
						icon={faSquareCheck}
						label={i18n.t("select_components")}
						color={"secondary"}
						onClick={onSelectComponentsClick}
					/>
				</>
			)}
			serverErrorCustomContent={
				<AsyncButton
					icon={faSquareCheck}
					label={i18n.t("select_components")}
					variant={"outlined"}
					onClick={onSelectComponentsClick}
				/>
			}
		/>
	);

	async function onSubmit(result: PartConfiguratorTreeResult) {
		const { configurationSessionId } = result;
		const propertyValues = await resolvePartConfiguratorTreeResultValues(configuratorType, result);
		openDialog(() => ({
			title: i18n.t("test_configuration"),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.catalogPartTestConfigurationResults.dialog"),
				data: {
					catalogPartRevisionId,
					productFamilyVersionId,
					configurationSessionId,
				},
				pathSegments: [i18n.t("test_configuration")],
			},
			content: (
				<CatalogPartTestConfiguratorResultsView
					configurationSessionId={configurationSessionId}
					configuratorType={configuratorType}
					propertyValues={propertyValues}
				/>
			),
		}));
	}

	function onSelectComponentsClick() {
		openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
			title: i18n.t("select_components_for_test_configurator"),
			size: "xl",
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.catalogPartTestComponentSelection.dialog"),
				data: {
					catalogPartRevisionId,
					productFamilyVersionId,
				},
				pathSegments: [i18n.t("select_components")],
			},
			content: (
				<CatalogPartRevisionConfiguratorSelectionsAsyncView
					catalogPartRevisionId={catalogPartRevisionId}
					productFamilyVersionId={productFamilyVersionId}
					onSubmitted={async () => {
						await closeDialog();
					}}
					onCancelled={async () => {
						await closeDialog({ confirmIfEdited: true });
					}}
					onDataDirtyStateChanged={onDataDirtyStateChanged}
				/>
			),
		}));
	}
};
